'use client';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @primitive Field
 * ROLE: none on Field (group-without-fieldset); inner control owns its native role.
 * ARIA: the label names a labelable control through label-for/id (useId); a
 *       group control (a role="group" a <label for> cannot name) opts in with
 *       useFieldLabelling() or Field.Group and is named by aria-labelledby
 *       instead. aria-describedby (description then error); aria-invalid;
 *       aria-required on a labelable control, "required" in words in the
 *       label of a group. Nothing is added to a layout container.
 *       Prop mode and slot mode wire the same way: Field.Label /
 *       Field.Description / Field.Error / Field.Control register themselves
 *       with the Field.
 * KEYBOARD: native browser tab/typing.
 * FOCUS: when error appears via async validation, focus moves to the field's input
 *        (handled by parent <Form> wrapper via id lookup).
 * LIVE-REGION: Field.Error has role="alert" + aria-live="polite".
 * PRM: no internal motion.
 * FORCED-COLORS: standard.
 * RTL: logical properties for spacing.
 * RSC: CLIENT (state tracking for ids + context).
 * WAI-APG: Form Validation pattern.
 * SUBSTRATE: @radix-ui/react-label (FormLabel — internal).
 */
import { Children, isValidElement, useCallback, useId, useMemo, useState, } from 'react';
import { FormLabel } from '../../_internal/FormLabel/FormLabel';
import { Box } from '../../../server/layout/Box/index';
import { Container } from '../../../server/layout/Container/index';
import { Grid } from '../../../server/layout/Grid/index';
import { Row } from '../../../server/layout/Row/index';
import { Stack } from '../../../server/layout/Stack/index';
import { FieldContext, hasMessageContent, } from './FieldContext';
import { FieldControl } from './FieldControl';
import { FieldDescription } from './FieldDescription';
import { FieldError } from './FieldError';
import { FieldGroup } from './FieldGroup';
import { FieldLabel } from './FieldLabel';
import styles from './Field.module.css';
// The HTML elements a <label for> can name.
const LABELABLE_ELEMENTS = new Set([
    'button',
    'input',
    'meter',
    'output',
    'progress',
    'select',
    'textarea',
]);
const LAYOUT_PRIMITIVES = new Set([Box, Container, Grid, Row, Stack]);
// Prop mode treats a single child as the control unless it plainly is not
// one: a non-labelable HTML element or a layout primitive gets nothing. A
// field whose content is a layout of several controls uses slot mode, with
// Field.Control on the real input or Field.Group around the controls.
function isControlCandidate(el) {
    if (typeof el.type === 'string')
        return LABELABLE_ELEMENTS.has(el.type);
    if (LAYOUT_PRIMITIVES.has(el.type))
        return false;
    const displayName = el.type.displayName;
    return !displayName?.startsWith('Field.');
}
function FieldInner({ label, description, error, required = false, layout = 'stacked', children, style, }) {
    const baseId = useId();
    const ids = useMemo(() => ({
        control: `${baseId}-field`,
        label: `${baseId}-label`,
        description: `${baseId}-desc`,
        error: `${baseId}-err`,
    }), [baseId]);
    const [registered, setRegistered] = useState({
        label: 0,
        description: 0,
        error: 0,
        control: 0,
        group: 0,
    });
    const registerPart = useCallback((slot) => {
        setRegistered((prev) => ({ ...prev, [slot]: prev[slot] + 1 }));
        return () => setRegistered((prev) => ({ ...prev, [slot]: prev[slot] - 1 }));
    }, []);
    // Prop mode: a single plain child is the control. Field.* compound members
    // wire themselves through context instead.
    const childArray = Children.toArray(children);
    const only = childArray.length === 1 ? childArray[0] : undefined;
    const wrapsControl = isValidElement(only) && isControlCandidate(only);
    const control = wrapsControl ? _jsx(FieldControl, { children: only }) : children;
    const hasLabelProp = label != null;
    const hasDescriptionProp = description != null;
    const hasErrorProp = hasMessageContent(error);
    // The props are known on the first render, so prop mode is wired before any
    // effect runs (and on the server); registration covers slot mode.
    const labelledBy = hasLabelProp || registered.label > 0 ? ids.label : undefined;
    const groupControl = registered.group > 0;
    const labelFor = !groupControl && (wrapsControl || registered.control > 0) ? ids.control : undefined;
    const hasDescription = hasDescriptionProp || registered.description > 0;
    const invalid = hasErrorProp || registered.error > 0;
    const describedByParts = [
        hasDescription ? ids.description : undefined,
        invalid ? ids.error : undefined,
    ].filter((id) => id !== undefined);
    const describedBy = describedByParts.length > 0 ? describedByParts.join(' ') : undefined;
    const ctxValue = useMemo(() => ({ ids, labelledBy, labelFor, groupControl, describedBy, invalid, required, registerPart }), [ids, labelledBy, labelFor, groupControl, describedBy, invalid, required, registerPart]);
    const labelEl = hasLabelProp ? (_jsx(FormLabel, { id: ids.label, htmlFor: labelFor, required: required, announceRequired: groupControl, children: label })) : null;
    const messages = (_jsxs(_Fragment, { children: [hasDescriptionProp ? _jsx(FieldDescription, { children: description }) : null, hasErrorProp ? _jsx(FieldError, { children: error }) : null] }));
    return (_jsx(FieldContext.Provider, { value: ctxValue, children: layout === 'inline' ? (_jsxs("div", { className: styles.inline, style: style, children: [_jsxs("div", { className: styles.inlineRow, children: [control, labelEl] }), messages] })) : (_jsxs("div", { className: styles.stacked, style: style, children: [labelEl, control, messages] })) }));
}
/**
 * Single-control field wrapper. Wires label, description, and error to the
 * inner control via id/aria attributes.
 *
 * Standard use (prop-driven):
 * ```tsx
 * <Field label="Email" description="We'll never share it." error={errors.email} required>
 *   <Input type="email" name="email" />
 * </Field>
 * ```
 *
 * Slot decomposition (10% case) — Field.Control goes on the real input, or
 * Field.Group around a set of controls:
 * ```tsx
 * <Field>
 *   <Field.Label>Email <em>(required)</em></Field.Label>
 *   <Field.Control><Input type="email" /></Field.Control>
 *   <Field.Description>We'll never share it.</Field.Description>
 *   <Field.Error>{errors.email}</Field.Error>
 * </Field>
 * ```
 */
export const Field = Object.assign(FieldInner, {
    Label: Object.assign(FieldLabel, { displayName: 'Field.Label' }),
    Description: Object.assign(FieldDescription, { displayName: 'Field.Description' }),
    Error: Object.assign(FieldError, { displayName: 'Field.Error' }),
    Control: Object.assign(FieldControl, { displayName: 'Field.Control' }),
    Group: Object.assign(FieldGroup, { displayName: 'Field.Group' }),
});
//# sourceMappingURL=Field.js.map